import React from "react";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaEye,
  FaLock,
  FaPaperPlane,
  FaPoll,
  FaVoteYea,
} from "react-icons/fa";

import {
  formatBallotDate,
  formatVoteDate,
  getBallotTimestamp,
  getVoteStatusInfo,
  getVoteTypeClass,
  getVoteTypeLabel,
} from "../utils/voteUserUtils";

export default function VoteCard({ vote, ballot, onParticipate, onViewAnswers }) {
  const status = getVoteStatusInfo(vote);
  const typeLabel = getVoteTypeLabel(vote?.type);
  const hasAnswered = Boolean(ballot);
  const canParticipate = !hasAnswered && status.key === "active";

  return (
    <article
      className={[
        "group relative overflow-hidden rounded-[1.75rem] border bg-white p-4 shadow-lg transition dark:bg-slate-950 sm:p-5",
        hasAnswered
          ? "border-emerald-200 shadow-emerald-100/60 dark:border-emerald-900/70 dark:shadow-black/10"
          : "border-slate-200 shadow-slate-200/60 hover:-translate-y-0.5 hover:shadow-xl dark:border-slate-800 dark:shadow-black/10",
      ].join(" ")}
    >
      <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-sky-500/10 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-12 -left-12 h-28 w-28 rounded-full bg-violet-500/10 blur-2xl" />

      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white shadow-md dark:bg-white dark:text-slate-950">
              {String(vote?.type || "").toLowerCase() === "sondage" ? (
                <FaPoll />
              ) : (
                <FaVoteYea />
              )}
            </div>

            <div className="min-w-0">
              <h3 className="line-clamp-2 text-base font-bold leading-6 text-slate-950 dark:text-white sm:text-lg">
                {vote?.title || "Vote sans titre"}
              </h3>

              <div className="mt-2 flex flex-wrap gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold ${getVoteTypeClass(
                    vote?.type
                  )}`}
                >
                  {typeLabel}
                </span>

                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold ${status.className}`}
                >
                  {status.label}
                </span>
              </div>
            </div>
          </div>

          {hasAnswered && (
            <div className="hidden shrink-0 rounded-full bg-emerald-100 p-2 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200 sm:block">
              <FaCheckCircle />
            </div>
          )}
        </div>

        {!!vote?.description && (
          <p
            className="mt-4 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300"
            title={vote.description}
          >
            {vote.description}
          </p>
        )}

        <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900/70">
          <div className="flex items-start gap-2 text-xs leading-5 text-slate-600 dark:text-slate-300">
            <FaCalendarAlt className="mt-1 shrink-0 text-slate-400" />
            <div>
              <p>
                <span className="font-bold text-slate-900 dark:text-white">
                  Début :
                </span>{" "}
                {formatVoteDate(vote?.start_time)}
              </p>

              <p>
                <span className="font-bold text-slate-900 dark:text-white">
                  Fin :
                </span>{" "}
                {formatVoteDate(vote?.end_time)}
              </p>
            </div>
          </div>
        </div>

        {hasAnswered && (
          <div className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200">
            Répondu le {formatBallotDate(getBallotTimestamp(ballot))}
          </div>
        )}

        <div className="mt-4">
          {hasAnswered ? (
            <button
              type="button"
              onClick={onViewAnswers}
              className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
            >
              <FaEye />
              Voir mes réponses
            </button>
          ) : canParticipate ? (
            <button
              type="button"
              onClick={onParticipate}
              className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-4 text-sm font-bold text-white shadow-lg shadow-violet-200 transition hover:-translate-y-0.5 hover:shadow-xl dark:shadow-black/20"
            >
              <FaPaperPlane />
              Répondre
            </button>
          ) : (
            <button
              type="button"
              disabled
              className="inline-flex min-h-12 w-full cursor-not-allowed items-center justify-center gap-2 rounded-2xl bg-slate-200 px-4 text-sm font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-400"
            >
              <FaLock />
              Non disponible
            </button>
          )}
        </div>
      </div>
    </article>
  );
}